import React, { useMemo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GalleryGrid } from '@components/GalleryComponents/GalleryGrid/GalleryGrid';
import {
  BackgroundGradient,
  CategoriesMenuItem,
  ECardsMenusSpacingStack,
  StyledFilterBox,
  PageContentWrapper,
  StyledReactPaginate,
  StyledReactDiv,
  StyledCTALink,
  FilterButtonWrapper,
  StyledFilterButton,
  StyledFilterCircle,
  StyledCloseIcon,
  StyledClearDiv,
  StyledClear,
  StyledFilterText,
  FilterMenuWrapperMobile,
  FilterMenuMobileDropShell,
  MobileFilterPanel,
  PanelFilterButtonContainer,
  StyledCommonFilterButton,
  StyledFilterIcon,
  StyledMobileCloseIcon,
  StyledMobileClose,
  FilterDivider,
  StyledBorder,
  RemoveAllWrapper,
  RemoveAllFilters,
  StyledFilterCloseIcon,
  PrintOnlineMenu,
  PrintOnlineMenuItem,
  CategoriesSelectColumnsWrapper,
  CategoriesSelect,
  CategoriesMenu,
  CategoriesMenuColumnsWrapper,
  CategoriesMenuItemPrev,
  StyledText,
  ECardsMenusSpacingStackPrev,
  CategoriesMenuItemWithImage,
  StyledSliderWrapper,
} from '@components/GalleryComponents/GalleryPageContent/GalleryPageContent.styles';
import {
  eCardLayouts,
  eCardCategories as eCardCategoriesAll,
  eCardColors,
  eCardPrints,
  eCardCategorySlugs,
  ECardCategorySlug,
  getCategoryLabel,
  eCardCollections,
} from '@utils/gallery.util';
import { useIsMounted } from '@hooks/useIsMounted';
import { useTheme } from '@styledComponents';
import ScrollMagic from 'scrollmagic';
import { gsap, Linear } from 'gsap';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import { Button } from '@components/Button';
import { Text } from '@components/typography';
import { SelectFilter, SelectFilterOptions } from '@components/SelectFilter';
import { SelectFilterMobile } from '@components/SelectFilterMobile';
import { Flex } from '@components/Flex';
import { FilterGroups, themeVariantFilter } from './GalleryPageContentFiltering';
import { GalleryPageHero } from './GalleryPageHero';
import { Column, Columns } from '@components/Columns';
import { SelectProps } from '@components/Select/Select';
import { snakeToCamel } from '@utils/strings.util';
import { useManagedSearchParam } from '@hooks/useManagedSearchParam';
import { useCallback } from 'react';
import { HorizontalSlider } from '@sections/shared/HorizontalSlider';
import { CloudImage } from '@components/CloudImage';
import { sortPrintGalleryCards } from '@utils/joyPrint.utils';
import { useTranslation } from 'react-i18next';
import { useRemoteFeatureFlag } from '@utils/remoteFeatureFlags/useRemoteFeatureFlag';

const analytics = require('../../../../static/js/joy');

export type ProcessFilterGroups = (filterGroups: FilterGroups, options: { hasPrints: boolean; hasFiltersApplied: boolean }) => FilterGroups;

interface Props {
  page: MarcomPageName;
  productType: GalleryProductType;
  cards: MarketingData;
  showModal: (isModalOpened: boolean) => void;
  hasPhotoECards?: boolean;
  displayHeroSection?: boolean;
  initialProductCount?: number;
  displayFilterTab?: boolean;
  defaultProductType?: GalleryProductType;
  sectionId?: string;
  displayCategoryImages?: boolean;
  categoryImagesData?: Record<string, string>;
  hideCategoryImagesScrollbar?: boolean;
  processFilterGroups?: ProcessFilterGroups;
  onShowDesignsWithMatchingPrintsToggle?: (hasPrints: boolean) => void;
  sortCards?: (cards: MarketingData) => MarketingData;
}

const getValidSearchParams = (filters: SelectFilterOptions, params: string[]) => {
  return params.filter((param) => filters.find((filter) => filter.value === param));
};

const usePrintsToggle = (
  productType: GalleryProductType,
  onShowDesignsWithMatchingPrintsToggle: Props['onShowDesignsWithMatchingPrintsToggle']
) => {
  const [matchingPrints, setMatchingPrints] = useManagedSearchParam('print');

  const [hasPrints, setHasPrints] = useState(
    () =>
      productType === 'Save the Date' ||
      productType === 'Invitation' ||
      (getValidSearchParams([{ name: 'Print', value: 'true' }], matchingPrints) || []).length > 0
  );

  useEffect(() => {
    onShowDesignsWithMatchingPrintsToggle?.(hasPrints);
  }, [hasPrints, onShowDesignsWithMatchingPrintsToggle]);

  return {
    matchingPrints,
    setMatchingPrints,
    hasPrints,
    setHasPrints,
  };
};

const checkIfFiltersAreApplied = (filters: Array<Array<string>>, booleanMap: Record<string, boolean>) => {
  return filters.some((filter) => filter.length) || Object.keys(booleanMap).some((key) => booleanMap[key]);
};

export const GalleryPageContent: React.FC<Props> = ({
  page,
  productType,
  processFilterGroups,
  cards: cardsProp,
  showModal,
  hasPhotoECards,
  onShowDesignsWithMatchingPrintsToggle,
  initialProductCount,
  categoryImagesData,
  hideCategoryImagesScrollbar,
  displayHeroSection = true,
  displayFilterTab = true,
  defaultProductType,
  sectionId = '',
  displayCategoryImages = false,
  sortCards,
}) => {
  const { t } = useTranslation('galleryPage');
  const GalleryPageContentData = t('galleryPage');
  const [categories, setCategories] = useManagedSearchParam('category');
  const [collections, setCollections] = useManagedSearchParam('collection');
  const [layouts, setLayouts] = useManagedSearchParam('layout');
  const [colors, setColors] = useManagedSearchParam('color');
  const { hasPrints, setMatchingPrints, setHasPrints } = usePrintsToggle(productType, onShowDesignsWithMatchingPrintsToggle);
  const categoriesTrans = t('categories');
  const eCardCategories = useMemo(
    () => (hasPhotoECards ? categoriesTrans : categoriesTrans.filter((category) => category.value !== 'photo')),
    [categoriesTrans, hasPhotoECards]
  );

  const cards = useMemo(() => {
    if (productType === 'Invitation' || productType === 'Save the Date') {
      return sortPrintGalleryCards(productType, cardsProp);
    }
    return sortCards ? sortCards(cardsProp) : Array.from(cardsProp).sort(() => Math.random() - 0.5);
  }, [sortCards, cardsProp, productType]);

  const isMobile = useThemeMediaQuery('below_sm');
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState(getValidSearchParams(eCardCategories, categories));
  const [selectedColors, setSelectedColors] = useState(getValidSearchParams(eCardColors, colors));
  const [selectedLayouts, setSelectedLayouts] = useState(getValidSearchParams(eCardLayouts, layouts));
  const [selectedCollections, setSelectedCollections] = useState(getValidSearchParams(eCardCollections, collections));
  const [isOpenLayout, setIsOpenLayout] = useState(false);
  const [isOpenCategory, setIsOpenCategory] = useState(false);
  const [isOpenCollection, setIsOpenCollection] = useState(false);
  const [isOpenColor, setIsOpenColor] = useState(false);
  const [lastOpenedMenu, setLastOpenedMenu] = useState('');
  const [filteredCards, setFilteredCardsState] = useState<FilterGroups>(() => {
    const filterGroups = themeVariantFilter.filter(cards, categories, colors, layouts, collections, false);

    return (
      processFilterGroups?.(filterGroups, {
        hasPrints,
        hasFiltersApplied: checkIfFiltersAreApplied([categories, colors, layouts, collections], { hasPrints }),
      }) || filterGroups
    );
  });
  const [hasClickedLoadMore, setHasClickedLoadMore] = useState(false);
  // temporary state to handle the matching prints filter active / inactive state
  const [isMatchingPrintFilterActive, setIsMatchingPrintFilterActive] = useState(hasPrints);

  const setFilteredCards = useCallback(
    (filterGroups: FilterGroups, hasFiltersApplied: boolean) => {
      setFilteredCardsState(processFilterGroups?.(filterGroups, { hasPrints, hasFiltersApplied }) || filterGroups);
    },
    [processFilterGroups, hasPrints]
  );
  const [openedMenu, setOpenedMenu] = useState(false);
  const [preSelectedFilters, setPreSelectedFilters] = useState<{
    layouts: string[];
    categories: string[];
    colors: string[];
    print: string[];
    collections: string[];
  }>({
    layouts: layouts,
    categories: categories,
    colors: colors,
    collections: collections,
    print: hasPrints ? ['print'] : [],
  });
  const scrollController = useScrollController();
  const theme = useTheme();
  const isMounted = useIsMounted();

  const designs = t('productTypes.designs');
  const eventProductTypes = t('eventProductTypes');
  const notFindingWhatLooking: string = t('galleryPage.notFindingWhatLooking');
  let resultsDontMatchAnything: string = t('galleryPage.resultsDontMatchAnything');

  let letUsKnow: string = t('galleryPage.letUsKnow');
  const pageData = GalleryPageContentData[productType];
  const sectionRef = useRef<HTMLDivElement>(null);
  const bgGradientRef = useRef<HTMLDivElement>(null);
  const [isHovered, setHovered] = useState(false);
  const [selectedFilterCount, setSelectedFilterCount] = useState(0);
  const marcomViewDemoSiteExperiment = useRemoteFeatureFlag('marcomViewDemoSiteExperiment', {
    skip: productType !== 'Wedding Website',
  });
  const isViewDemoSiteExperimentTreatment = marcomViewDemoSiteExperiment.value === 'treatment';

  const filterCount = useMemo(
    () =>
      Object.values(preSelectedFilters)
        .map((selected) => selected.length)
        .reduce((a, b) => a + b, 0),

    [preSelectedFilters]
  );

  const TOTAL_ITEM_PER_PAGE: number = initialProductCount || (isMobile ? 20 : 30);

  useEffect(() => {
    const groupedFilters = themeVariantFilter.filter(
      cards,
      selectedCategorySlugs,
      selectedColors,
      selectedLayouts,
      selectedCollections,
      hasPrints
    );
    setFilteredCards(
      groupedFilters,
      checkIfFiltersAreApplied([selectedCategorySlugs, selectedColors, selectedLayouts, selectedCollections], { hasPrints })
    );
    setLayouts(selectedLayouts);
    setCategories(selectedCategorySlugs);
    setColors(selectedColors);
    setCollections(selectedCollections);
    setMatchingPrints(hasPrints ? ['true'] : []);
    setSelectedFilterCount(filterCount);
  }, [
    cards,
    selectedCategorySlugs,
    selectedColors,
    selectedLayouts,
    selectedCollections,
    hasPrints,
    setFilteredCards,
    setSelectedCategorySlugs,
    setSelectedColors,
    setSelectedLayouts,
    preSelectedFilters,
    setLayouts,
    setCategories,
    setColors,
    setMatchingPrints,
    setCollections,
    filterCount,
    categories,
  ]);

  const trackEvent = (boolTrackEvent: boolean, forceTrack?: boolean, hasPrintsOverride?: boolean, lastOpenedMenuOverride?: string) => {
    if ((boolTrackEvent && selectedFilterCount) || (forceTrack && boolTrackEvent)) {
      const layouts = preSelectedFilters.layouts.map((item) => (item === 'aloha' ? 'Single Page' : 'Multi Page'));
      analytics.segment.sendTelemetryFilterEvent('ThemesFiltered', {
        type: 'track',
        category: `newmarketing.${eventProductTypes[productType]}`,
        action: 'ThemesFiltered',
        label: lastOpenedMenuOverride ? lastOpenedMenuOverride : lastOpenedMenu,
        extraInfo: {
          numResultsMatchingAny: filteredCards.match.length,
          layout: layouts,
          category: preSelectedFilters.categories.toString(),
          color: preSelectedFilters.colors.toString(),
          hasMatchingPrints: hasPrintsOverride ? hasPrintsOverride : hasPrints,
        },
      });
    }
  };

  const clearAllFilters = () => {
    preSelectedFilters.categories = [];
    preSelectedFilters.layouts = [];
    preSelectedFilters.colors = [];
    preSelectedFilters.print = [];
    preSelectedFilters.collections = [];
    setPreSelectedFilters(preSelectedFilters);
    setSelectedCategorySlugs(preSelectedFilters.categories);
    setSelectedLayouts(preSelectedFilters.layouts);
    setSelectedColors(preSelectedFilters.colors);
    setSelectedCollections(preSelectedFilters.collections);
    setHasPrints(false);
    setHovered(false);
  };

  const handleClickLoadMore = useCallback(() => {
    setSelectedPage(selectedPage + 1);
    setHasClickedLoadMore(true);
  }, [selectedPage]);

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    const sectionEl = sectionRef.current;
    const bgGradientEl = bgGradientRef?.current;
    let sectionTimeline: ReturnType<typeof gsap.timeline> | null = null;
    const bgColorFrom = theme.colors.neutra1;
    const bgColorTo = 'rgba(249, 245, 240, 0)';
    if (scrollController && isMounted && sectionEl && bgGradientEl) {
      if (scrollController) {
        sectionTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
        sectionTimeline.fromTo(
          [bgGradientEl],
          { background: `linear-gradient(to bottom, ${bgColorFrom} 47.95%, ${bgColorTo} 70.28%)` },
          {
            background: `linear-gradient(to bottom, ${bgColorTo} 47.95%, ${bgColorTo} 70.28%)`,
            duration: 1,
          },
          0
        );
      }
    }
    const initScene = () => {
      if (sectionEl && scrollController) {
        scene = new ScrollMagic.Scene({
          triggerElement: sectionEl,
          triggerHook: 'onLeave',
          duration: 450,
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            sectionTimeline?.progress(event.progress);
          });
      }
    };
    initScene();
  }, [scrollController, isMounted, theme]);

  const categoryItemsWithImage = eCardCategories.map((category, index) => ({
    id: category.name,
    item: (
      <Flex flexDirection="column" key={index} alignItems="center">
        <CategoriesMenuItemWithImage
          id={snakeToCamel(`ecardsStoreCategory-${category.value}`)}
          isSelected={category.value === preSelectedFilters.categories[0]}
          onClick={(event) => {
            event.preventDefault();
            preSelectedFilters.categories = [category.value];
            setPreSelectedFilters(preSelectedFilters);
            setSelectedCategorySlugs([category.value]);
            setSelectedPage(1);
            trackEvent(true, true, true, 'categories');
          }}
          skipRedirectWithTelemetry
        >
          {<CloudImage src={categoryImagesData?.[category?.value] ?? ''} width="88px" />}
        </CategoriesMenuItemWithImage>
        <StyledText variant="featureCopy17" tagName="p" color="neutra5" textAlign={'center'}>
          {category.name}
        </StyledText>
      </Flex>
    ),
  }));

  return (
    <PageContentWrapper
      isOpen={openedMenu}
      as="section"
      ref={sectionRef}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        setIsOpenLayout(false);
        setIsOpenCategory(false);
        setIsOpenColor(false);
        setIsOpenCollection(false);
        trackEvent(isOpenLayout || isOpenCategory || isOpenColor);
      }}
      displayHeroSection={displayHeroSection}
      id={sectionId}
    >
      {displayHeroSection && (
        <>
          <BackgroundGradient ref={bgGradientRef} />
          <GalleryPageHero page={page} productType={productType} />
        </>
      )}
      {designs === productType ? (
        <>
          <ECardsMenusSpacingStack>
            <Flex justifyContent="center" paddingTop={8}>
              <StyledFilterBox marginLeft={1}>
                {productType === designs ? (
                  <SelectFilter
                    selectedOptions={preSelectedFilters.layouts}
                    options={eCardLayouts}
                    showIcon={true}
                    isOpen={isOpenLayout}
                    selectType="layout"
                    selectLabel="All Layouts"
                    handleOpen={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setIsOpenLayout(!isOpenLayout);
                      setIsOpenCollection(false);
                      setIsOpenCategory(false);
                      setIsOpenColor(false);
                      setLastOpenedMenu('layout');
                    }}
                    onClickedOption={(selectedLayouts) => {
                      preSelectedFilters.layouts = selectedLayouts;
                      setPreSelectedFilters(preSelectedFilters);
                      setSelectedLayouts(selectedLayouts);
                      setSelectedPage(1);
                    }}
                  />
                ) : null}
                <SelectFilter
                  selectedOptions={selectedCollections}
                  options={eCardCollections}
                  isOpen={isOpenCollection}
                  selectType="collection"
                  selectLabel="Collection"
                  handleOpen={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsOpenCollection(!isOpenCollection);
                    setIsOpenCategory(false);
                    setIsOpenLayout(false);
                    setIsOpenColor(false);
                    setLastOpenedMenu('collection');
                  }}
                  onClickedOption={(selectedCollections) => {
                    preSelectedFilters.collections = selectedCollections;
                    setPreSelectedFilters(preSelectedFilters);
                    setSelectedCollections(selectedCollections);
                    setSelectedPage(1);
                  }}
                />
                <SelectFilter
                  selectedOptions={selectedCategorySlugs}
                  options={eCardCategories}
                  isOpen={isOpenCategory}
                  selectType="category"
                  selectLabel="Category"
                  handleOpen={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsOpenCategory(!isOpenCategory);
                    setIsOpenCollection(false);
                    setIsOpenLayout(false);
                    setIsOpenColor(false);
                    setLastOpenedMenu('category');
                  }}
                  onClickedOption={(selectedCategories) => {
                    preSelectedFilters.categories = selectedCategories;
                    setPreSelectedFilters(preSelectedFilters);
                    setSelectedCategorySlugs(selectedCategories);
                    setSelectedPage(1);
                  }}
                />
                <SelectFilter
                  selectedOptions={preSelectedFilters.colors}
                  options={eCardColors}
                  isOpen={isOpenColor}
                  selectType="color"
                  selectLabel="Color"
                  handleOpen={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setIsOpenColor(!isOpenColor);
                    setIsOpenCollection(false);
                    setIsOpenLayout(false);
                    setIsOpenCategory(false);
                    setLastOpenedMenu('color');
                  }}
                  onClickedOption={(selectedColors) => {
                    preSelectedFilters.colors = selectedColors;
                    setPreSelectedFilters(preSelectedFilters);
                    setSelectedColors(selectedColors);
                    setSelectedPage(1);
                  }}
                />
                <CategoriesMenuItem
                  key={'bold'}
                  id="matching-prints"
                  isSelected={hasPrints || isMatchingPrintFilterActive}
                  onClick={(event) => {
                    event.preventDefault();
                    preSelectedFilters.print = [];
                    setPreSelectedFilters(preSelectedFilters);
                    setHasPrints(false);
                    setIsMatchingPrintFilterActive((current) => !current);
                    setSelectedPage(1);
                    setLastOpenedMenu('prints');
                    event.stopPropagation();
                    trackEvent(false, true, true, 'prints');
                  }}
                >
                  <Text variant="featureCopy17" tagName="p" color="neutra5">
                    {GalleryPageContentData.matchingPrints}
                  </Text>
                </CategoriesMenuItem>
                {selectedFilterCount > 0 ? (
                  <StyledClearDiv
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    onClick={() => {
                      clearAllFilters();
                    }}
                  >
                    <StyledCloseIcon name="notAttending" color={isHovered ? 'linkHover' : 'neutra5'} />
                    <StyledClear>{GalleryPageContentData.clearAll}</StyledClear>
                  </StyledClearDiv>
                ) : null}
              </StyledFilterBox>
            </Flex>
          </ECardsMenusSpacingStack>

          <FilterButtonWrapper>
            <StyledFilterButton
              onClick={(event) => {
                event.stopPropagation();
                setOpenedMenu(true);
                showModal(true);
              }}
              id="mobile-filter-button"
              variant={'secondary'}
            >
              {selectedFilterCount ? (
                <StyledFilterCircle variant="featureHed17" tagName="p" color="neutra5" textAlign="center">
                  {selectedFilterCount}
                </StyledFilterCircle>
              ) : (
                <StyledFilterIcon size={24} name="filter" color={'neutra5'} />
              )}
              <StyledFilterText variant="featureHed17" tagName="p" color="neutra5" textAlign="center">
                {GalleryPageContentData.filter}
              </StyledFilterText>
            </StyledFilterButton>
          </FilterButtonWrapper>
          <FilterMenuWrapperMobile isOpen={!!openedMenu}>
            <StyledMobileClose
              onClick={() => {
                setOpenedMenu(false);
                showModal(false);
              }}
            >
              <StyledMobileCloseIcon name="notAttending" color={'neutra5'} />
            </StyledMobileClose>
            <FilterMenuMobileDropShell isOpen={!!openedMenu}>
              {openedMenu && (
                <>
                  <MobileFilterPanel>
                    {productType === designs ? (
                      <SelectFilterMobile
                        selectedOptions={preSelectedFilters.layouts}
                        options={eCardLayouts}
                        showIcon={true}
                        selectType="layout"
                        selectLabel="Layout"
                        onClickedOption={(selectedLayouts) => {
                          preSelectedFilters.layouts = selectedLayouts;
                          setPreSelectedFilters(preSelectedFilters);
                          setSelectedLayouts(selectedLayouts);
                          setSelectedPage(1);
                          setLastOpenedMenu('layout');
                        }}
                      />
                    ) : null}
                    <SelectFilterMobile
                      selectedOptions={preSelectedFilters.collections}
                      options={eCardCollections}
                      showIcon={false}
                      selectType="collection"
                      selectLabel="Collection"
                      onClickedOption={(selectedCollections) => {
                        preSelectedFilters.collections = selectedCollections;
                        setPreSelectedFilters(preSelectedFilters);
                        setSelectedCollections(selectedCollections);
                        setSelectedPage(1);
                        setLastOpenedMenu('collection');
                      }}
                    />
                    <SelectFilterMobile
                      selectedOptions={preSelectedFilters.categories}
                      options={eCardCategories}
                      showIcon={false}
                      selectType="category"
                      selectLabel="Category"
                      onClickedOption={(selectedCategories) => {
                        preSelectedFilters.categories = selectedCategories;
                        setPreSelectedFilters(preSelectedFilters);
                        setSelectedCategorySlugs(selectedCategories);
                        setSelectedPage(1);
                        setLastOpenedMenu('category');
                      }}
                    />
                    <SelectFilterMobile
                      selectedOptions={preSelectedFilters.colors}
                      options={eCardColors}
                      showIcon={false}
                      selectType="color"
                      selectLabel="Color"
                      padding={'43px'}
                      onClickedOption={(selectedColors) => {
                        preSelectedFilters.colors = selectedColors;
                        setPreSelectedFilters(preSelectedFilters);
                        setSelectedColors(selectedColors);
                        setSelectedPage(1);
                        setLastOpenedMenu('color');
                      }}
                    />
                    <SelectFilterMobile
                      selectedOptions={isMatchingPrintFilterActive ? ['print'] : preSelectedFilters.print}
                      options={eCardPrints}
                      showIcon={false}
                      selectType="print"
                      selectLabel="Matching Prints"
                      onClickedOption={(selectedPrint) => {
                        preSelectedFilters.print = [];
                        setPreSelectedFilters(preSelectedFilters);
                        setHasPrints(false);
                        setIsMatchingPrintFilterActive((current) => !current);
                        setSelectedPage(1);
                        setLastOpenedMenu('print');
                      }}
                    />
                  </MobileFilterPanel>
                </>
              )}
            </FilterMenuMobileDropShell>
          </FilterMenuWrapperMobile>
        </>
      ) : (
        <ECardsMenusSpacingStackPrev>
          {displayFilterTab && (
            <PrintOnlineMenu key={`print-digital-menu-${isMounted}`} isRightSelected={hasPrints} isMounted={isMounted}>
              <PrintOnlineMenuItem
                id="ecardsStoreOnlineFilter"
                href="#"
                $isActive={!hasPrints}
                onClick={(event) => {
                  event.preventDefault();
                  setPreSelectedFilters({ ...preSelectedFilters, print: [] });
                  setHasPrints(false);
                  setSelectedPage(1);
                  trackEvent(!hasPrints, true, true, 'prints');
                }}
              >
                {GalleryPageContentData.online}
              </PrintOnlineMenuItem>
              <PrintOnlineMenuItem
                id="ecardsStorePrintFilter"
                href="#"
                $isActive={hasPrints}
                onClick={(event) => {
                  event.preventDefault();
                  setPreSelectedFilters({ ...preSelectedFilters, print: ['print'] });
                  setHasPrints(true);
                  setSelectedPage(1);
                  trackEvent(true, true, true, 'prints');
                }}
              >
                {GalleryPageContentData.printNOnline}
              </PrintOnlineMenuItem>
            </PrintOnlineMenu>
          )}
          <CategoriesSelectColumnsWrapper>
            <Columns>
              <Column width={12}>
                <CategoriesSelect<React.FC<SelectProps<ECardCategorySlug>>>
                  value={(preSelectedFilters.categories[0] as ECardCategorySlug) || ''}
                  options={eCardCategorySlugs}
                  labelFunction={(categorySlug) => getCategoryLabel(categorySlug)}
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
                    let selectedCategoriesPrev: string[] = [];
                    if (value) {
                      selectedCategoriesPrev = [value];
                    }
                    preSelectedFilters.categories = selectedCategoriesPrev;
                    setPreSelectedFilters(preSelectedFilters);
                    setSelectedCategorySlugs(selectedCategoriesPrev);
                    setSelectedPage(1);
                    trackEvent(true, true, true, 'categories');
                  }}
                  selectAllLabel="All"
                />
              </Column>
            </Columns>
          </CategoriesSelectColumnsWrapper>
          {!displayCategoryImages && (
            <CategoriesMenuColumnsWrapper>
              <Columns>
                <Column>
                  <CategoriesMenu>
                    {eCardCategories.map((category, index) => (
                      <CategoriesMenuItemPrev
                        key={index}
                        id={snakeToCamel(`ecardsStoreCategory-${category.value}`)}
                        isSelected={category.value === preSelectedFilters.categories[0]}
                        onClick={(event) => {
                          event.preventDefault();
                          preSelectedFilters.categories = [category.value];
                          setPreSelectedFilters(preSelectedFilters);
                          setSelectedCategorySlugs([category.value]);
                          setSelectedPage(1);
                          trackEvent(true, true, true, 'categories');
                        }}
                        skipRedirectWithTelemetry
                      >
                        <StyledText variant="featureCopy17" tagName="p" color="neutra5">
                          {category.name}
                        </StyledText>
                      </CategoriesMenuItemPrev>
                    ))}
                  </CategoriesMenu>
                </Column>
              </Columns>
            </CategoriesMenuColumnsWrapper>
          )}
        </ECardsMenusSpacingStackPrev>
      )}
      {displayCategoryImages && (
        <StyledSliderWrapper>
          <HorizontalSlider items={categoryItemsWithImage} hideScrollbar={hideCategoryImagesScrollbar} />
        </StyledSliderWrapper>
      )}
      {openedMenu ? (
        <PanelFilterButtonContainer className="skip-margin-button">
          <StyledCommonFilterButton
            id={'filter-close-button'}
            variant={'primary'}
            onClick={() => {
              setSelectedLayouts(preSelectedFilters.layouts);
              setSelectedCategorySlugs(preSelectedFilters.categories);
              setSelectedColors(preSelectedFilters.colors);
              let hasPrints: boolean = false;
              if (preSelectedFilters.print.length > 0) {
                hasPrints = true;
              }
              setHasPrints(hasPrints);
              setSelectedPage(1);
              setOpenedMenu(false);
              showModal(false);
              trackEvent(true);
            }}
          >
            {GalleryPageContentData.filter}
          </StyledCommonFilterButton>
        </PanelFilterButtonContainer>
      ) : null}
      <GalleryGrid
        productType={productType}
        defaultProductType={defaultProductType}
        hasPrints={hasPrints}
        showViewDemoSite={isViewDemoSiteExperimentTreatment}
        cards={
          !selectedFilterCount || (initialProductCount && !hasClickedLoadMore)
            ? filteredCards.match.slice(0, selectedPage * TOTAL_ITEM_PER_PAGE)
            : filteredCards.match
        }
      />
      <RemoveAllWrapper show={!filteredCards.match.length} marginTop={[5]}>
        <Text variant="sectionEyeBrow24" tagName="p" color="neutra6" textAlign="center">
          {notFindingWhatLooking}
        </Text>
        <Text variant="featureCopy17" tagName="p" color="neutra5" textAlign="center">
          {GalleryPageContentData.tryUsingFewer}
        </Text>
        <RemoveAllFilters
          id="removeAllFilters"
          onClick={(event) => {
            event.preventDefault();
            clearAllFilters();
          }}
        >
          <Text marginBottom={'2px'} variant="featureCopy17" tagName="div" color="neutra5" textAlign="center">
            <StyledFilterCloseIcon size={16} name={'notAttending'} color={'neutra5'} />
            {GalleryPageContentData.clearAllFilters}
          </Text>
        </RemoveAllFilters>
      </RemoveAllWrapper>
      {(!initialProductCount || hasClickedLoadMore) && (
        <>
          <FilterDivider isDisplayed={!!filteredCards.noMatch.length}>
            <StyledBorder />
            <Text variant="featureCopy17" tagName="p" color="neutra5" textAlign="center" marginTop={9}>
              {resultsDontMatchAnything}
            </Text>
          </FilterDivider>
          <GalleryGrid
            hasPrints={hasPrints}
            productType={productType}
            cards={filteredCards.noMatch.slice(0, selectedPage * TOTAL_ITEM_PER_PAGE)}
            defaultProductType={defaultProductType}
            showViewDemoSite={isViewDemoSiteExperimentTreatment}
          />
        </>
      )}
      <StyledReactPaginate
        isDisabled={
          (!selectedFilterCount
            ? filteredCards.match.length < TOTAL_ITEM_PER_PAGE * selectedPage
            : filteredCards.noMatch.length < TOTAL_ITEM_PER_PAGE * selectedPage) && !(initialProductCount && !hasClickedLoadMore)
        }
        marginTop={[5]}
      >
        {'loadMoreButtonLink' in pageData ? (
          <Button
            skipRedirectWithTelemetry
            id={'load-more-button'}
            variant={isMobile ? 'primary' : 'secondary'}
            forwardedAs="a"
            href={pageData.loadMoreButtonLink}
            target="_blank"
          >
            {pageData.loadMoreButton}
          </Button>
        ) : (
          <Button
            skipRedirectWithTelemetry
            id={'load-more-button'}
            variant={isMobile ? 'primary' : 'secondary'}
            onClick={handleClickLoadMore}
          >
            {pageData.loadMoreButton}
          </Button>
        )}
      </StyledReactPaginate>
      <StyledReactDiv
        isDisabled={
          (!selectedFilterCount
            ? filteredCards.match.length >= TOTAL_ITEM_PER_PAGE * selectedPage
            : filteredCards.noMatch.length >= TOTAL_ITEM_PER_PAGE * selectedPage) ||
          (!!initialProductCount && !hasClickedLoadMore)
        }
        marginTop={[5]}
      >
        <Text variant="featureHed17" tagName="p" color="neutra5" textAlign="center">
          {notFindingWhatLooking}
        </Text>
        <StyledCTALink id="GalleryThemeSurvey" typography="featureHed17" href="https://withjoy.typeform.com/to/lIVZl3rB">
          {letUsKnow}
        </StyledCTALink>
      </StyledReactDiv>
    </PageContentWrapper>
  );
};
